import { css } from "@emotion/css";
import { Button, Collapse, type PrimaryPageAction, ListTitle, SearchInput } from "@octopusdeploy/design-system-components";
import { FilterIcon } from "@octopusdeploy/design-system-icons";
import { space } from "@octopusdeploy/design-system-tokens";
import type { ProjectResource, ResourceCollection, RunbookResource, TagSetResource } from "@octopusdeploy/octopus-server-client";
import { canCommitTo, getGitRefDescription, HasRunbooksInGit, isGitBranchResource, Permission } from "@octopusdeploy/octopus-server-client";
import { type GitRefQueryParams, links, type QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { ProjectGitHubAppAuthCheck } from "~/areas/library/components/GitConnections/GitHubAppAuthCheck";
import { SampleProjectTourContext } from "~/areas/projects/components/ProjectLayout/SampleProjectTour/SampleProjectTour";
import { useProjectContext } from "~/areas/projects/context";
import { FilterBuilder } from "~/areas/tenants/components/Filtering/FilterBuilder/FilterBuilder";
import type { Filter, FilterValue } from "~/areas/tenants/components/Filtering/FilterBuilder/filterBuilderTypes";
import { createTagSetFilters, getIncludedTagSetValues } from "~/areas/tenants/components/Filtering/FilterBuilder/filterBuilderTypes";
import type { DoBusyTask, Errors } from "~/components/DataBaseComponent";
import { DataBaseComponent } from "~/components/DataBaseComponent";
import Dialog from "~/components/Dialog/Dialog";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { actions as dialogActions } from "~/components/Dialog/store";
import MarkdownDescription from "~/components/MarkdownDescription";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import type { MenuItem } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { PageContent } from "~/components/PageContent/PageContent";
import PermissionCheck, { isAllowed, isAllowedToRunGitRunbook } from "~/components/PermissionCheck/PermissionCheck";
import { tagSummaryByNames } from "~/components/tagSummary";
import { useRequiredContext } from "~/hooks/index";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import { PageHeaderBranchSelector } from "../BranchSelector/PageHeaderBranchSelector";
import { useProjectRunbooksGitRef } from "../BranchSelector/useProjectGitRef";
import AddRunbook from "./AddRunbook";
import { FilteredByChips } from "./CurrentFilterSelection/CurrentFilterSelection";
import { LoadMoreList } from "./LoadMoreList";
import { getPermissionDeniedTooltipText, RunNowButton } from "./RunNowButton";
import RunbooksOnboarding from "./RunbooksOnboarding";
import { UnmigratedDraftsCallout } from "./UnmigratedDraftsCallout";
import type { RunbooksFilterState } from "./hooks/useRunbooksFilterState";
import { useRunbooksState } from "./hooks/useRunbooksState";
import MigrateProjectRunbooksBanner from "./migrations/MigrateProjectRunbooksBanner";
export interface FilterableRunbookCollection extends ResourceCollection<RunbookResource> {
    IsFiltered: boolean;
}
export interface RunbooksPageQueryParams extends GitRefQueryParams {
    runbookTags?: string[];
}
interface RunbooksPageProps {
    queryParams: RunbooksPageQueryParams;
    setQueryParams: QueryParamValuesSetter<RunbooksPageQueryParams>;
}
export class RunbooksPage extends DataBaseComponent<RunbooksPageProps> {
    constructor(props: RunbooksPageProps) {
        super(props);
        this.state = {};
    }
    componentDidMount() {
        this.clearErrors();
    }
    render() {
        return <RunbooksPageInternal {...this.props} doBusyTask={this.doBusyTask} busy={this.state.busy} errors={this.errors}/>;
    }
    static displayName = "RunbooksPage";
}
interface RunbooksPageInternalProps extends RunbooksPageProps {
    doBusyTask: DoBusyTask;
    busy?: Promise<unknown>;
    errors?: Errors;
}
function RunbooksPageInternal({ doBusyTask, busy, errors, queryParams, setQueryParams }: RunbooksPageInternalProps) {
    const navigation = useSpaceAwareNavigation();
    const projectContext = useProjectContext();
    const onboardingContext = useRequiredContext(SampleProjectTourContext);
    const { isOpen: isAddRunbookDialogOpen, openDialog: openAddRunbookDialog, closeAllDialogs: closeAddRunbookDialog } = useDialogTrigger();
    const { model: project } = projectContext.state;
    const [gitRef, setGitRef, gitRefValidationResult] = useProjectRunbooksGitRef(project, queryParams, setQueryParams);
    const hasGitRunbooks = HasRunbooksInGit(project.PersistenceSettings);
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const { runbooks, tagSets, filterState, take, pageSize, runbooksActions, reload } = useRunbooksState(doBusyTask, queryParams, setQueryParams, project, gitRef, hasGitRunbooks, () => onboardingContext.continueSampleProjectTour());
    const [debouncedPartialName, setDebouncedPartialName] = useDebouncedExternalState(filterState.filterByName ?? "", (value) => runbooksActions.setRunbooksFilter({ ...filterState, filterByName: value }), 500);
    const runbookTagFilters = useMemo(() => {
        if (!tagSets || tagSets.length === 0)
            return [];
        // Filter out FreeText tag sets
        const nonFreeTextTagSets = tagSets.filter((ts) => ts.Type !== "FreeText");
        return createTagSetFilters(nonFreeTextTagSets, filterState.filterByTags, []);
    }, [tagSets, filterState.filterByTags]);
    const handleTagFilterChange = useCallback((filterValues: FilterValue[]) => {
        const runbookTags = getIncludedTagSetValues(filterValues);
        runbooksActions.setRunbooksFilter({ ...filterState, filterByTags: runbookTags });
    }, [filterState, runbooksActions]);
    const handleToggleFilters = useCallback(() => {
        setFiltersExpanded(!filtersExpanded);
    }, [filtersExpanded]);
    const addRunbookPageAction: PrimaryPageAction = {
        type: "button",
        label: "Add Runbook",
        hasPermissions: isAllowed({ permission: Permission.RunbookEdit, project: project.Id, wildcard: true }),
        onClick: openAddRunbookDialog,
        disabled: hasGitRunbooks && !canCommitTo(gitRefValidationResult?.GitRef),
        extraContext: hasGitRunbooks && gitRefValidationResult?.GitRef && !canCommitTo(gitRefValidationResult?.GitRef)
            ? `Cannot create a Runbook on a ${isGitBranchResource(gitRefValidationResult?.GitRef) ? "protected branch" : getGitRefDescription(gitRef)}`
            : undefined,
    };
    const filters = runbooks && (runbooks.Items.length > 0 || runbooks.IsFiltered)
        ? {
            inputs: [
                <RunbooksPageFilterSection key="runbook-filter-section" runbookTagFilters={runbookTagFilters} handleTagFilterChange={handleTagFilterChange} filtersExpanded={filtersExpanded} onToggleFilters={handleToggleFilters} debouncedPartialName={debouncedPartialName} setDebouncedPartialName={setDebouncedPartialName} filterState={filterState} tagSets={tagSets}/>,
            ],
        }
        : undefined;
    return (<PageContent busy={busy} errors={errors} header={{
            title: "Runbooks",
            primaryAction: runbooks?.Items?.length !== 0 || runbooks.IsFiltered ? addRunbookPageAction : undefined,
            contextSelector: hasGitRunbooks ? <PageHeaderBranchSelector project={project} gitRef={gitRef} setGitRef={setGitRef}/> : undefined,
        }} filters={filters} callout={{
            type: "custom",
            content: (<>
                        {runbooks && <MigrateProjectRunbooksBanner hasNoRunbooks={runbooks.Items.length === 0 && !runbooks.IsFiltered}/>}
                        <UnmigratedDraftsCallout project={project} gitRef={gitRef}/>
                        {HasRunbooksInGit(project.PersistenceSettings) && <ProjectGitHubAppAuthCheck project={project} permission={Permission.RunbookView}/>}
                    </>),
        }}>
            <Dialog open={isAddRunbookDialogOpen}>
                <AddRunbook projectId={project.Id} onProcessCreated={(id) => {
            closeAddRunbookDialog();
            navigation.navigate(links.runbookRootRedirect.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug, runbookId: id }));
        }}/>
            </Dialog>
            {runbooks && (<>
                    {runbooks.Items.length === 0 && !runbooks.IsFiltered ? (<RunbooksOnboarding actionButtons={<PermissionCheck permission={Permission.RunbookEdit} project={project.Id} tenant="*">
                                    <Button importance="primary" label="Add Runbook" onClick={openAddRunbookDialog} disabled={hasGitRunbooks && !canCommitTo(gitRefValidationResult?.GitRef)}/>
                                </PermissionCheck>}/>) : (<LoadMoreList loadingMore={!!busy} items={runbooks.Items} renderRow={(item) => (<RunbooksPageRow project={project} runbook={item} tagSets={tagSets} reload={async () => {
                        await reload();
                    }} hasGitRunbooks={hasGitRunbooks} gitRef={gitRef}/>)} getAccessibleName={(runbook) => runbook.Name} getNavigationUrl={(runbook) => links.runbookRootRedirect.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug, runbookId: runbook.Id })} showLoadMore={runbooks.Items.length < runbooks.TotalResults} onLoadMore={() => {
                    runbooksActions.setPageSize(pageSize);
                }} showSearch={false} onFilterChanged={() => { }}/>)}
                </>)}
        </PageContent>);
}
interface RunbooksPageFilterSectionProps {
    runbookTagFilters: Filter[];
    handleTagFilterChange: (filterValues: FilterValue[]) => void;
    filtersExpanded: boolean;
    onToggleFilters: () => void;
    debouncedPartialName?: string;
    setDebouncedPartialName: (value: string) => void;
    filterState: RunbooksFilterState;
    tagSets: TagSetResource[];
}
function RunbooksPageFilterSection({ runbookTagFilters, handleTagFilterChange, filtersExpanded, onToggleFilters, debouncedPartialName, setDebouncedPartialName, filterState, tagSets }: RunbooksPageFilterSectionProps) {
    return (<section className={filterSectionStyles.filterSection} aria-label="runbook-filter-selection">
            <div className={filterSectionStyles.filterControls}>
                <div className={filterSectionStyles.filterControlsLeft}>
                    <div className={filterSectionStyles.searchInputContainer}>
                        <SearchInput placeholder="Filter by name..." value={debouncedPartialName ?? ""} onChange={setDebouncedPartialName} accessibleName="Filter by runbook name"/>
                    </div>
                    {runbookTagFilters.length > 0 && <Button label={`${filtersExpanded ? "Hide" : "Show"} advanced filters`} icon={<FilterIcon size={20}/>} onClick={onToggleFilters} importance="quiet"/>}
                    <FilteredByChips filterState={filterState} tagSets={tagSets}/>
                </div>
            </div>
            <Collapse in={filtersExpanded} timeout="auto" unmountOnExit>
                <div className={filterSectionStyles.advancedFiltersContainer}>
                    <div className={filterSectionStyles.filterBuilder}>{runbookTagFilters.length > 0 && <FilterBuilder filters={runbookTagFilters} onChange={handleTagFilterChange} display="full"/>}</div>
                </div>
            </Collapse>
        </section>);
}
interface RunbooksPageRowProps {
    project: ProjectResource;
    runbook: RunbookResource;
    tagSets: TagSetResource[];
    reload: () => Promise<void>;
    hasGitRunbooks: boolean;
    gitRef?: string;
}
function RunbooksPageRow({ project, runbook, tagSets, reload, hasGitRunbooks, gitRef }: RunbooksPageRowProps) {
    const dispatch = useDispatch();
    const runbookDescription = runbook.Description && <MarkdownDescription markup={runbook.Description}/>;
    const runbookTags = runbook.RunbookTags ?? [];
    const overFlowActions: Array<MenuItem | MenuItem[]> = [];
    if (!hasGitRunbooks) {
        const CloneDialog = () => (<AddRunbook projectId={project.Id} onProcessCreated={(id) => {
                dispatch(dialogActions.closeAll());
                reload();
            }} cloneId={runbook.Id}/>);
        const clonePermission = {
            permission: Permission.RunbookEdit,
            projectGroup: project.ProjectGroupId,
            wildcard: true,
        };
        overFlowActions.push(OverflowMenuItems.dialogItem("Clone", <CloneDialog />, clonePermission));
    }
    overFlowActions.push([
        OverflowMenuItems.navItem("Audit Trail", links.auditPage.generateUrl({ projects: [project.Id], documentTypes: ["Runbooks"], regardingAny: [runbook.Id] }), {
            permission: Permission.EventView,
            wildcard: true,
        }),
    ]);
    const runAction = (<RunNowButton spaceId={runbook.SpaceId} isDisabled={hasGitRunbooks && !isAllowedToRunGitRunbook(project, gitRef)} tooltip={hasGitRunbooks && !isAllowedToRunGitRunbook(project, gitRef) ? getPermissionDeniedTooltipText(gitRef) : undefined} projectSlug={project.Slug} projectId={project.Id} runbookId={runbook.Id} buttonType="secondary"/>);
    return <RunbookRowContent title={runbook.Name} tags={runbookTags.length > 0 ? tagSummaryByNames(tagSets, runbookTags).node : undefined} description={runbookDescription} overflowMenuItems={overFlowActions} secondaryAction={runAction}/>;
}
interface RunbookRowContentProps {
    overflowMenuItems: Array<MenuItem | MenuItem[]>;
    secondaryAction: React.ReactNode;
    title: string;
    tags?: React.ReactNode;
    description: React.ReactNode;
}
function RunbookRowContent({ title, tags, description, overflowMenuItems, secondaryAction }: RunbookRowContentProps) {
    return (<div className={RunbookRowContentStyles.container}>
            <div className={RunbookRowContentStyles.titleAndDescriptionContainer}>
                <ListTitle alignment="left">{title}</ListTitle>
                {description}
                {tags}
            </div>
            <div className={RunbookRowContentStyles.actionsContainer}>
                {secondaryAction && secondaryAction}
                {overflowMenuItems && overflowMenuItems.length > 0 && <OverflowMenu menuItems={overflowMenuItems}/>}
            </div>
        </div>);
}
const RunbookRowContentStyles = {
    container: css({
        display: "flex",
        flexWrap: "wrap",
        gap: space[16],
        [`@media (min-width: 769px)`]: {
            flexWrap: "nowrap",
        },
    }),
    titleAndDescriptionContainer: css({
        display: "flex",
        flexGrow: 1,
        flexDirection: "column",
        justifyContent: "center",
    }),
    actionsContainer: css({
        alignItems: "center",
        display: "flex",
        flex: "0 0 100%",
        [`@media (min-width: 375px)`]: {
            flex: "0 0 auto",
        },
    }),
};
const filterSectionStyles = {
    filterSection: css({
        width: "100%",
        marginBottom: space[16],
    }),
    filterControls: css({
        display: "flex",
        justifyContent: "flex-start",
        gap: space[8],
    }),
    filterControlsLeft: css({
        display: "flex",
        gap: space[16],
        flexWrap: "wrap",
        alignItems: "center",
    }),
    searchInputContainer: css({
        width: "19rem",
    }),
    advancedFiltersContainer: css({
        paddingTop: space[16],
    }),
    filterBuilder: css({
        display: "flex",
        gap: space[16],
    }),
};
